import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import core from '@gin-betting/core';
import { createSelector } from 'reselect';

import type { State } from 'types';
import { unpinStream } from '../../actions';
import {
    selectLastPlayedStreamMatchId,
    selectMatchIdFullScreen,
    selectPinnedStreamInfo,
} from '../../selectors';
import { InitPlayerFn, ViewerMode } from '../../types';
import useStream from '../Stream/useStream';

const {
    utils: { isInnerPage },
} = core;

interface Output {
    pinnedStreamUrl: string | null;
    pinnedStreamTitle: string | null;
    initPlayer: InitPlayerFn;
    onToggleStreamFullscreen: VoidFn;
    onStreamUnpin: VoidFn;
    onTogglePlayStatus: (isPlaying: boolean) => void;
    viewerMode: ViewerMode;
    isHiddenFullScreenBtn: boolean;
}

const selectProps = createSelector(
    selectMatchIdFullScreen,
    selectPinnedStreamInfo,
    selectLastPlayedStreamMatchId,
    (matchIdFullScreen, pinnedStreamInfo, lastPlayedStreamMatchId) => ({
        matchIdFullScreen,
        pinnedStreamInfo,
        lastPlayedStreamMatchId,
    })
);

function usePersistentStream(): Output {
    const dispatch = useDispatch();
    const { pathname } = useLocation();

    const {
        matchIdFullScreen,
        pinnedStreamInfo: {
            pinnedStreamMatchId,
            pinnedStreamTitle,
            pinnedStreamUrl,
        },
        lastPlayedStreamMatchId,
    } = useSelector((state: State) => selectProps(state, {}));

    const {
        initPlayer,
        onStreamUnpin,
        onTogglePlayStatus,
        onToggleStreamFullscreen,
        viewerMode,
    } = useStream({
        matchId: pinnedStreamMatchId,
    });

    useEffect(() => {
        if (isInnerPage(pathname)) {
            dispatch(unpinStream());
        }
    }, [dispatch, pathname]);

    useEffect(() => {
        if (
            pinnedStreamMatchId &&
            lastPlayedStreamMatchId &&
            pinnedStreamMatchId !== lastPlayedStreamMatchId
        ) {
            dispatch(unpinStream());
        }
    }, [dispatch, lastPlayedStreamMatchId, pinnedStreamMatchId]);

    return {
        pinnedStreamUrl,
        pinnedStreamTitle,
        viewerMode,
        initPlayer,
        onToggleStreamFullscreen,
        onStreamUnpin,
        onTogglePlayStatus,
        isHiddenFullScreenBtn: pinnedStreamMatchId !== matchIdFullScreen,
    };
}

export default usePersistentStream;
